import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import FlyupLabel from 'common/FlyupLabel';
import { withStyles } from 'theme/withStyles';
import Spinner from 'common/Spinner';
import ErrorMsg from 'common/ErrorMsg';
import Help from 'common/Help';
import Tooltip from 'common/Tooltip';
import { withFieldProps } from 'hooks/useFieldProps';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

import inputStyles from './styles/input';
import rootStyles from './styles/root';

const styles = ({ forms, palette, spacing, typography }) => ({
  root: {
    ...rootStyles({ spacing }),
    '& .react-tel-input': {
      '& .flag-dropdown': {
        '&.open': {
          '& .selected-flag': {
            background: 'transparent',
          },
          zIndex: 20,
        },
        '&:hover': {
          background: 'transparent',
        },
        background: 'transparent',
        border: 'none',
        left: 0,
      },
      '& .form-control': {
        ...inputStyles({ forms, palette, typography }),
        paddingLeft: 30,
      },
      '& .selected-flag': {
        '&:hover, &:focus': {
          background: 'transparent',
        },

        paddingLeft: 0,
      },
    },
  },
});

const formatForAPI = (value) => {
  const aryCleanedValue = value.replace(/[()-]/g, '').split(' ');
  const [areaCode, ...restValues] = aryCleanedValue;
  const formattedValue = `${areaCode} ${restValues.join('')}`;

  return formattedValue;
};

const PhoneNumber = ({
  classes,
  error,
  fieldDomId,
  help,
  loading,
  name,
  onBlur,
  onChange,
  onFocus,
  onPaste,
  placeholder,
  readOnly,
  required,
  tooltip,
  validate,
  value,
}) => {
  const [didFocus, setDidFocus] = useState(false);
  return (
    <>
      <div className={classnames('form-group', { error, loading }, classes.root)}>
        <Spinner loading={loading} />
        <FlyupLabel
          fieldName={name}
          error={error}
          value={value}
          tooltip={tooltip}
          htmlFor={fieldDomId}
          required={required}
          focus={didFocus}
        />
        <PhoneInput
          country="us"
          preferredCountries={['us']}
          excludeCountries={['cu', 'ir', 'kp', 'sy', 'sd', 'ss']}
          onChange={(val, country, e, formattedValue) => {
            const updatedValue = formatForAPI(formattedValue);
            onChange(name, updatedValue);
          }}
          inputClass="form-control"
          name={name}
          id={fieldDomId}
          value={value}
          placeholder={placeholder}
          readOnly={readOnly}
          required={required}
          aria-invalid={error}
          onPaste={onPaste}
          masks={{ cn: '... .... ....', id: '... .... ....' }}
          onBlur={() => {
            onBlur();
            validate();
          }}
          onFocus={() => {
            setDidFocus(true);
            onFocus();
          }}
          autocomplete="tel"
          aria-describedby={
            classnames({
              [`help-${name}`]: help,
              [`error-${name}`]: error,
            }) || undefined
          }
        />
        <Tooltip tooltip={tooltip} labeledBy={`label-${fieldDomId}`} />
        <Help help={help} id={`help-${name}`} />
      </div>

      <ErrorMsg name={name} />
    </>
  );
};

PhoneNumber.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  error: PropTypes.bool.isRequired,
  fieldDomId: PropTypes.string.isRequired,
  help: PropTypes.string.isRequired,
  loading: PropTypes.bool.isRequired,
  name: PropTypes.string.isRequired,
  onBlur: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onFocus: PropTypes.func.isRequired,
  onPaste: PropTypes.func.isRequired,
  placeholder: PropTypes.string.isRequired,
  readOnly: PropTypes.bool.isRequired,
  required: PropTypes.bool.isRequired,
  tooltip: PropTypes.string.isRequired,
  validate: PropTypes.func.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

const PhoneNumberStyled = withStyles(styles)(PhoneNumber);

export { PhoneNumberStyled as PhoneNumberBase };

export default withFieldProps()(PhoneNumberStyled);
